import { type Coordinate, type GoogleGeocoderAddressComponent, type GooglePlaceResult, getLicensePlateOrCobliIdOrPlaceholder, type Vehicle } from '@cobliteam/common-types';
import { GeocoderAddressComponentTypes, type InputActivity, type InputDestinationAPI } from './Definitions';

export const DEFAULT_PAGE_SIZE = 2147483647;
export const DEFAULT_PAGE = 0;

export const mapAddressComponents = (addressComponents: GoogleGeocoderAddressComponent[]) =>
  Object.values(GeocoderAddressComponentTypes).reduce((result: Partial<Record<GeocoderAddressComponentTypes, string>>, value: GeocoderAddressComponentTypes) => {
    const addressComponent = addressComponents.find((component) => component.types[0] === value);

    if (addressComponent) {
      result[value] = addressComponent.short_name;
    }

    return result;
  }, {});

export const getAddressComponent = (componentType: GeocoderAddressComponentTypes, componentName: string): GoogleGeocoderAddressComponent => ({
  short_name: componentName,
  long_name: componentName,
  types: [componentType],
});

export const addressComponentConcat = (divider: string, componentOne?: string, componentTwo?: string) => {
  if (!(componentOne || componentTwo)) {
    return undefined;
  }
  return `${componentOne || ''}${componentOne && componentTwo ? divider : ''}${componentTwo || ''}`;
};

export const getFormattedAddress = (inputDestination: InputDestinationAPI) => {
  const { city, state, street_address: streetAddress, neighborhood, postal_code: postalCode, street_complement: streetComplement } = inputDestination;

  const neighborhoodAndCity = addressComponentConcat(', ', neighborhood, city);
  const stateAndPostalCode = addressComponentConcat(', ', state, postalCode);

  const components = [streetAddress, streetComplement, neighborhoodAndCity, stateAndPostalCode];

  return components.filter((component) => component).join(' - ');
};

export const mapCoordinate = (coordinate: Coordinate) => [coordinate.longitude, coordinate.latitude];

export const getVehicleString = (vehicle: Vehicle) => {
  const vehicleInfo = {
    id: (vehicle && getLicensePlateOrCobliIdOrPlaceholder(vehicle)) || 'Sem dispositivo',
    name: vehicle?.driver?.name || 'Sem motorista associado',
  };

  return `${vehicleInfo.id} - ${vehicleInfo.name}`;
};

export const concatSpreadsheetColumns = (row: string[], indexes: number[]) =>
  indexes
    .map((index) => row[index])
    .join(' ')
    .trim();

export const defaultPlace: GooglePlaceResult = {
  name: '',
  formatted_address: '',
};

/**
 * Whether the activity now carries a value for the field the warning refers to.
 *
 * An unknown field counts as still missing: the importer may grow new ones, and treating a field we
 * cannot check as resolved would understate the count and hide a real loss.
 */
const IMPORT_WARNING_FILLED_IN = new Map<string, (activity: InputActivity) => boolean>([
  ['time_window', (activity) => Boolean(activity.startTimeWindow)],
  ['load_size', (activity) => activity.loadSize !== undefined],
]);

const isImportWarningStillOpen = (activity: InputActivity, field: string): boolean => {
  // A `Map` and not an object literal: `field` comes from the API, and an object lookup walks the
  // prototype chain. A field named `toString` or `constructor` would return an inherited function
  // and silently resolve the warning, and `valueOf` or `__proto__` would throw inside the render.
  const filledIn = IMPORT_WARNING_FILLED_IN.get(field);

  return filledIn ? !filledIn(activity) : true;
};

/**
 * Number of imported rows that are still missing a value the importer could not interpret.
 *
 * The row still imports — only the cell the importer did not understand is missing — so nothing
 * else on the screen signals the loss. Counting rows rather than warnings is deliberate: the banner
 * tells the user how many stops to check against the sheet, and a stop with two dropped cells is
 * still one stop to look at.
 *
 * A warning stops counting once the user fills the field in. Without that, the banner would keep
 * naming stops the user had already fixed, and the only way out would be dismissing it — which
 * teaches people to dismiss the banner without reading it.
 *
 * The map holds exactly the two fields the importer reports today (`MapSpreadsheetToActivities` in
 * `atlas-api` emits `time_window` and `load_size`, and nothing else). A field it does not know stays
 * open rather than being guessed at — writing entries for warnings that do not exist would be
 * guessing at semantics we have not seen.
 *
 * Resolution also needs the cell to be on screen, and only one tab has it: `RoutingActivityItem`
 * edits both the time window and the load, while `ActivityItem` — the activities-without-a-route tab
 * — edits neither of them (it edits name, address, phone, start date and start hour). There the
 * warning stays until dismissed, which is the honest outcome: its message tells the user to check
 * the spreadsheet, and that tab has no field for either dropped value.
 */
export const countActivitiesWithImportWarnings = (activities: InputActivity[]): number =>
  activities.filter((activity) => Boolean(activity.warnings?.some((warning) => isImportWarningStillOpen(activity, warning.field)))).length;

/**
 * Whether the imported-values banner should be on screen.
 *
 * Two conditions, and both matter. `wasShownAfterImport` starts `false` on every mount and is only
 * turned on by the import-success transition — that is what stops the banner from coming back after
 * the tab is rebuilt, since `PlanningPageTab` swaps tabs with a `switch` while the import result
 * stays in Redux.
 *
 * The count is passed in rather than computed here so the caller can reuse it for the message, and
 * it has to be taken from the form list rather than from the import result: imports accumulate —
 * `SpreadsheetImportCard` concatenates — so a second, clean import would zero a count taken from the
 * last result while the dropped rows of the first one are still on screen and on their way to the
 * save.
 */
export const shouldShowImportWarnings = (openWarningCount: number, wasShownAfterImport: boolean): boolean => wasShownAfterImport && openWarningCount > 0;
